import { useState, useEffect, useRef } from "react";

// ── Theme ─────────────────────────────────────────────────────────
const ACCENT = "#64ffda";
const BG = "#080808";
const SURFACE = "#101010";
const BORDER = "#1c1c1c";

const PAGES = ["Home", "About", "Skills", "Projects", "Experience", "Education", "Contact"];

// ── Data ──────────────────────────────────────────────────────────
const SKILLS_DATA = [
  {
    cat: "Frontend",
    items: ["HTML5", "CSS3", "JavaScript", "React", "Next.js"],
  },
  {
    cat: "Backend",
    items: ["Node.js", "Express", "REST APIs"],
  },
  {
    cat: "Zoho Ecosystem",
    items: ["Zoho CRM", "Zoho Creator", "Zoho Books", "Zoho Desk", "Deluge Script", "Zoho Catalyst"],
  },
  {
    cat: "DevOps (Learning)",
    items: ["Linux", "Docker", "Git", "CI/CD"],
  },
];

const PROJECTS_DATA = [
  {
    title: "Zoho CRM Pipeline Automation",
    category: "Zoho",
    desc: "Custom workflow automation and module configuration for a B2B sales pipeline using Deluge scripting and blueprints.",
    tags: ["Zoho CRM", "Deluge", "Automation"],
    status: "Production",
  },
  {
    title: "React Portfolio Website",
    category: "Web App",
    desc: "Personal developer portfolio built with React, hosted on Zoho Catalyst with a custom domain and Zoho Mail.",
    tags: ["React", "Zoho Catalyst", "CSS"],
    status: "Live",
  },
  {
    title: "Node.js Task Manager API",
    category: "API",
    desc: "RESTful API built with Express for a task management app — JWT authentication, MongoDB, and modular routing.",
    tags: ["Node.js", "Express", "MongoDB"],
    status: "In Progress",
  },
  {
    title: "Docker Dev Environment",
    category: "DevOps",
    desc: "Containerized multi-service development setup using Docker Compose — app, database, and reverse proxy all wired up.",
    tags: ["Docker", "Linux", "Compose"],
    status: "Learning",
  },
];

const EXPERIENCE_DATA = [
  {
    period: "2023 – Present",
    role: "Zoho Developer",
    company: "Your Company Name",
    points: [
      "Implemented and customized Zoho CRM for 5+ clients with tailored modules and workflows",
      "Built Deluge scripts to automate complex business logic and reduce manual entry",
      "Configured and integrated Zoho Books, Desk, and Creator across client environments",
      "Reduced manual process time by ~40% through workflow and blueprint automation",
    ],
  },
  {
    period: "2022 – 2023",
    role: "Junior Web Developer",
    company: "Previous Company",
    points: [
      "Developed responsive web interfaces using HTML, CSS, and JavaScript",
      "Built and maintained React components for client-facing projects",
      "Collaborated on Node.js backend APIs and REST integrations",
    ],
  },
];

const EDUCATION_DATA = [
  {
    degree: "Bachelor of Engineering",
    field: "Computer Science / IT",
    college: "Your College Name",
    year: "2020 – 2024",
  },
  {
    degree: "Higher Secondary Certificate",
    field: "Computer Science & Mathematics",
    college: "Your School Name",
    year: "2018 – 2020",
  },
];

const CERTS = [
  "Zoho CRM Certified",
  "React – The Complete Guide",
  "Docker for Developers",
  "Linux Fundamentals",
];

// ── Particle Canvas ───────────────────────────────────────────────
function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let animId;
    let pts = [];

    const init = () => {
      c.width = c.offsetWidth;
      c.height = c.offsetHeight;
      pts = Array.from({ length: 55 }, () => ({
        x: Math.random() * c.width,
        y: Math.random() * c.height,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: Math.random() * 1.2 + 0.4,
        o: Math.random() * 0.35 + 0.08,
      }));
    };
    init();
    window.addEventListener("resize", init);

    const loop = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > c.width) p.vx *= -1;
        if (p.y < 0 || p.y > c.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(100,255,218,${p.o})`;
        ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < 95) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(100,255,218,${0.055 * (1 - d / 95)})`;
            ctx.lineWidth = 0.4;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(loop);
    };
    loop();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", init);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    />
  );
}

// ── Typing animation ──────────────────────────────────────────────
function useTyping(words, spd = 85, pause = 1800) {
  const [display, setDisplay] = useState("");
  const st = useRef({ wi: 0, ci: 0, del: false, waiting: false });

  useEffect(() => {
    const s = st.current;
    if (s.waiting) return;
    const cur = words[s.wi];

    const tick = () => {
      if (!s.del) {
        const next = cur.slice(0, s.ci + 1);
        setDisplay(next);
        s.ci++;
        if (s.ci === cur.length) {
          s.waiting = true;
          setTimeout(() => {
            s.del = true;
            s.waiting = false;
          }, pause);
        }
      } else {
        const next = cur.slice(0, s.ci - 1);
        setDisplay(next);
        s.ci--;
        if (s.ci === 0) {
          s.del = false;
          s.wi = (s.wi + 1) % words.length;
        }
      }
    };

    const t = setTimeout(tick, s.del ? spd / 2 : spd);
    return () => clearTimeout(t);
  });

  return display;
}

// ── Shared styles ─────────────────────────────────────────────────
const css = {
  wrap: {
    maxWidth: 980,
    margin: "0 auto",
    padding: "5rem 2rem",
  },
  label: {
    display: "block",
    fontSize: ".72rem",
    fontWeight: 700,
    letterSpacing: ".16em",
    textTransform: "uppercase",
    color: ACCENT,
    marginBottom: ".6rem",
  },
  h2: {
    fontSize: "clamp(1.6rem,3vw,2.2rem)",
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: "#f0f0f0",
    marginBottom: "3rem",
    marginTop: 0,
  },
  card: {
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: "1.4rem",
    transition: "border-color .2s, transform .2s",
  },
  tag: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 4,
    fontSize: ".72rem",
    fontWeight: 500,
    letterSpacing: ".05em",
    background: "#161616",
    color: "#888",
    border: "1px solid #222",
  },
  muted: {
    color: "#666",
    fontSize: ".9rem",
    lineHeight: 1.75,
    margin: 0,
  },
};

// ── Navbar ────────────────────────────────────────────────────────
function Navbar({ page, navigate }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:4px;} ::-webkit-scrollbar-track{background:#080808;} ::-webkit-scrollbar-thumb{background:#222;border-radius:2px;}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        .nav-link:hover{color:#f0f0f0 !important;}
        .btn-primary:hover{opacity:.88;}
        .btn-ghost:hover{border-color:#444 !important;color:#f0f0f0 !important;}
        .skill-card:hover{border-color:${ACCENT} !important;}
        .proj-card:hover{transform:translateY(-4px);border-color:#2a2a2a !important;}
        .edu-card:hover{border-color:${ACCENT} !important;}
        input:focus,textarea:focus{border-color:${ACCENT} !important;outline:none;}
        input::placeholder,textarea::placeholder{color:#333;}
      `}</style>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        height: 60, padding: "0 2rem", display: "flex",
        alignItems: "center", justifyContent: "space-between",
        background: "rgba(8,8,8,0.96)", backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${BORDER}`,
        fontFamily: "'Outfit', sans-serif",
      }}>
        <div
          onClick={() => navigate("Home")}
          style={{ fontWeight: 800, fontSize: "1.15rem", letterSpacing: "-0.02em", color: ACCENT, cursor: "pointer" }}
        >
          AK
        </div>
        {/* Desktop links */}
        <div style={{ display: "flex", gap: "1.8rem" }}>
          {PAGES.map((p) => (
            <span
              key={p}
              className="nav-link"
              onClick={() => navigate(p)}
              style={{
                fontSize: ".78rem", fontWeight: page === p ? 700 : 400,
                letterSpacing: ".1em", textTransform: "uppercase",
                color: page === p ? "#f0f0f0" : "#555",
                cursor: "pointer", transition: "color .15s",
              }}
            >
              {page === p ? `› ${p}` : p}
            </span>
          ))}
        </div>
      </nav>
    </>
  );
}

// ── Home ──────────────────────────────────────────────────────────
function HomePage({ navigate }) {
  const typed = useTyping([
    "Zoho Developer",
    "DevOps Learner",
    "React Builder",
    "Problem Solver",
  ]);

  return (
    <div style={{
      position: "relative", minHeight: "calc(100vh - 60px)",
      display: "flex", alignItems: "center",
      fontFamily: "'Outfit', sans-serif",
    }}>
      <Particles />
      <div style={{
        ...css.wrap, position: "relative", zIndex: 1,
        animation: "fadeUp .6s ease both",
      }}>
        <span style={css.label}>Available for opportunities · Madurai, TN</span>
        <h1 style={{
          fontSize: "clamp(2.6rem,7vw,4.5rem)",
          fontWeight: 800, letterSpacing: "-0.04em",
          lineHeight: 1.08, color: "#f0f0f0",
          marginBottom: "1.5rem", marginTop: ".5rem",
        }}>
          Hi, I'm{" "}
          <span style={{ color: ACCENT }}>Abimanyu</span>
          <br />
          <span style={{ color: "#2a2a2a" }}>—&nbsp;</span>
          <span style={{ color: "#999" }}>
            {typed}
            <span style={{ color: ACCENT, animation: "blink 1s step-end infinite" }}>|</span>
          </span>
        </h1>
        <p style={{
          ...css.muted, maxWidth: 500,
          fontSize: ".98rem", marginBottom: "2.5rem",
        }}>
          Junior developer with 2 years building & customizing Zoho solutions.
          Now expanding into DevOps — Linux, Docker, and cloud infrastructure.
        </p>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "4rem" }}>
          <button
            className="btn-primary"
            onClick={() => navigate("Projects")}
            style={{
              padding: "11px 26px", background: ACCENT, color: BG,
              border: "none", borderRadius: 6, fontWeight: 700,
              fontSize: ".88rem", cursor: "pointer",
              fontFamily: "'Outfit', sans-serif", letterSpacing: ".04em",
              transition: "opacity .15s",
            }}
          >
            View Projects →
          </button>
          <button
            className="btn-ghost"
            onClick={() => navigate("Contact")}
            style={{
              padding: "11px 26px", background: "transparent",
              color: "#888", border: "1px solid #282828",
              borderRadius: 6, fontWeight: 500, fontSize: ".88rem",
              cursor: "pointer", fontFamily: "'Outfit', sans-serif",
              transition: "all .15s",
            }}
          >
            Get in Touch
          </button>
        </div>
        <div style={{ display: "flex", gap: "2.5rem", flexWrap: "wrap" }}>
          {[
            ["2+", "Years Exp"],
            ["5+", "Zoho Projects"],
            ["4+", "Web Apps"],
            ["∞", "Still Learning"],
          ].map(([n, l]) => (
            <div key={l}>
              <div style={{ fontSize: "1.7rem", fontWeight: 800, color: "#f0f0f0", lineHeight: 1 }}>{n}</div>
              <div style={{ fontSize: ".7rem", color: "#444", letterSpacing: ".1em", textTransform: "uppercase", marginTop: ".3rem" }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── About ─────────────────────────────────────────────────────────
function AboutPage() {
  return (
    <div style={{ ...css.wrap, fontFamily: "'Outfit', sans-serif", animation: "fadeUp .5s ease both" }}>
      <span style={css.label}>Who I am</span>
      <h2 style={css.h2}>About Me</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: "3rem", alignItems: "start" }}>
        <div>
          <div style={{
            width: 90, height: 90, borderRadius: "50%",
            background: "#0d1a16", border: `2px solid ${ACCENT}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "2rem", fontWeight: 800, color: ACCENT, marginBottom: "2rem",
          }}>
            A
          </div>
          <p style={{ ...css.muted, marginBottom: "1.1rem" }}>
            I'm <strong style={{ color: "#d0d0d0", fontWeight: 600 }}>Abimanyu NK</strong>, a junior developer
            based in Madurai, Tamil Nadu. I've spent the last 2 years deep in the Zoho
            ecosystem — customizing CRM pipelines, automating workflows with Deluge, and
            integrating Zoho Books, Desk, and Creator for real clients.
          </p>
          <p style={{ ...css.muted, marginBottom: "1.1rem" }}>
            Now I'm leveling up into DevOps — picking up Linux fundamentals and Docker
            to understand the full deployment story. My goal is to become a well-rounded
            developer who can build, ship, and maintain production systems end to end.
          </p>
          <p style={css.muted}>
            Outside of work, I'm tinkering with side projects, following DevOps trends,
            and slowly making Docker Compose feel like home.
          </p>
          <div style={{ display: "flex", gap: ".75rem", marginTop: "1.75rem", flexWrap: "wrap" }}>
            <a href="https://github.com" target="_blank" rel="noreferrer"
              style={{ color: ACCENT, textDecoration: "none", fontSize: ".82rem", fontWeight: 600, border: `1px solid ${ACCENT}44`, padding: "7px 16px", borderRadius: 6 }}>
              GitHub ↗
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer"
              style={{ color: "#888", textDecoration: "none", fontSize: ".82rem", border: "1px solid #252525", padding: "7px 16px", borderRadius: 6 }}>
              LinkedIn ↗
            </a>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
          {[
            ["Location", "Madurai, Tamil Nadu"],
            ["Email", "abimanyu@yourdomain.com"],
            ["Experience", "2 Years"],
            ["Speciality", "Zoho + DevOps"],
            ["Languages", "Tamil, English"],
            ["Status", "Open to work 🟢"],
          ].map(([k, v]) => (
            <div key={k} style={{
              ...css.card,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontSize: ".72rem", color: "#444", letterSpacing: ".1em", textTransform: "uppercase" }}>{k}</span>
              <span style={{ fontSize: ".88rem", color: "#bbb", fontWeight: 500 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Skills ────────────────────────────────────────────────────────
function SkillsPage() {
  return (
    <div style={{ ...css.wrap, fontFamily: "'Outfit', sans-serif", animation: "fadeUp .5s ease both" }}>
      <span style={css.label}>What I work with</span>
      <h2 style={css.h2}>Skills</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "1.25rem" }}>
        {SKILLS_DATA.map(({ cat, items }) => (
          <div
            key={cat}
            className="skill-card"
            style={{ ...css.card }}
          >
            <div style={{ ...css.label, marginBottom: "1rem" }}>{cat}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
              {items.map((i) => (
                <span key={i} style={css.tag}>{i}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Progress bars */}
      <div style={{ marginTop: "3rem" }}>
        <div style={{ ...css.label, marginBottom: "1.5rem" }}>Proficiency</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
          {[
            ["Zoho Ecosystem", 88],
            ["HTML / CSS / JS", 80],
            ["React / Next.js", 70],
            ["Node.js / Express", 65],
            ["Linux", 40],
            ["Docker", 38],
          ].map(([skill, pct]) => (
            <div key={skill}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: ".4rem" }}>
                <span style={{ fontSize: ".82rem", color: "#999" }}>{skill}</span>
                <span style={{ fontSize: ".78rem", color: ACCENT, fontWeight: 600 }}>{pct}%</span>
              </div>
              <div style={{ height: 3, background: "#161616", borderRadius: 2 }}>
                <div style={{ height: "100%", width: `${pct}%`, background: ACCENT, borderRadius: 2, opacity: .85 }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        marginTop: "2.5rem", padding: "1.4rem",
        background: "#0c1a15", border: `1px solid ${ACCENT}22`,
        borderLeft: `3px solid ${ACCENT}`, borderRadius: 10,
      }}>
        <span style={{ ...css.label, marginBottom: ".5rem" }}>Currently Learning</span>
        <p style={css.muted}>
          Deepening DevOps knowledge — Linux system administration, Docker containerization,
          and CI/CD pipelines. Next target: Kubernetes basics and cloud fundamentals.
        </p>
      </div>
    </div>
  );
}

// ── Projects ──────────────────────────────────────────────────────
function ProjectsPage() {
  const [hover, setHover] = useState(null);
  const catColor = {
    Zoho: ACCENT,
    "Web App": "#f0f0f0",
    API: "#aaa",
    DevOps: "#ffc857",
  };
  const statusBg = {
    Production: "#0d1f12",
    Live: "#0d1f12",
    "In Progress": "#1a1400",
    Learning: "#0e0e1a",
  };
  const statusColor = {
    Production: "#4ade80",
    Live: "#4ade80",
    "In Progress": "#fbbf24",
    Learning: "#818cf8",
  };

  return (
    <div style={{ ...css.wrap, fontFamily: "'Outfit', sans-serif", animation: "fadeUp .5s ease both" }}>
      <span style={css.label}>Things I've built</span>
      <h2 style={css.h2}>Projects</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: "1.25rem" }}>
        {PROJECTS_DATA.map((p, i) => (
          <div
            key={i}
            className="proj-card"
            style={{ ...css.card, cursor: "pointer" }}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <span style={{
                fontSize: ".7rem", fontWeight: 700, letterSpacing: ".12em",
                textTransform: "uppercase", color: catColor[p.category] || "#aaa",
              }}>
                {p.category}
              </span>
              <span style={{
                fontSize: ".68rem", padding: "2px 9px", borderRadius: 4,
                background: statusBg[p.status] || "#111",
                color: statusColor[p.status] || "#666",
                fontWeight: 600,
              }}>
                {p.status}
              </span>
            </div>
            <h3 style={{ fontSize: ".98rem", fontWeight: 700, color: "#e8e8e8", marginBottom: ".75rem", lineHeight: 1.35 }}>
              {p.title}
            </h3>
            <p style={{ ...css.muted, fontSize: ".84rem", marginBottom: "1.25rem" }}>{p.desc}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {p.tags.map((t) => <span key={t} style={css.tag}>{t}</span>)}
            </div>
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center", marginTop: "3rem" }}>
        <a href="https://github.com" target="_blank" rel="noreferrer"
          style={{
            color: ACCENT, textDecoration: "none", fontSize: ".84rem",
            fontWeight: 600, border: `1px solid ${ACCENT}44`,
            padding: "10px 22px", borderRadius: 6, display: "inline-block",
          }}>
          See all projects on GitHub ↗
        </a>
      </div>
    </div>
  );
}

// ── Experience ────────────────────────────────────────────────────
function ExperiencePage() {
  return (
    <div style={{ ...css.wrap, fontFamily: "'Outfit', sans-serif", animation: "fadeUp .5s ease both" }}>
      <span style={css.label}>Where I've worked</span>
      <h2 style={css.h2}>Experience</h2>
      <div style={{ position: "relative", paddingLeft: "1.75rem" }}>
        <div style={{
          position: "absolute", left: 0, top: 8, bottom: 8,
          width: 1, background: BORDER,
        }} />
        {EXPERIENCE_DATA.map((e, i) => (
          <div key={i} style={{ position: "relative", marginBottom: "3rem" }}>
            <div style={{
              position: "absolute", left: "-1.95rem", top: 7,
              width: 9, height: 9, borderRadius: "50%",
              background: ACCENT, border: `2px solid ${BG}`,
            }} />
            <span style={{ ...css.label, marginBottom: ".3rem" }}>{e.period}</span>
            <h3 style={{ fontSize: "1.08rem", fontWeight: 700, color: "#f0f0f0", marginBottom: ".2rem" }}>
              {e.role}
            </h3>
            <div style={{ fontSize: ".82rem", color: "#444", marginBottom: "1rem" }}>{e.company}</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: ".55rem" }}>
              {e.points.map((pt, j) => (
                <li key={j} style={{ ...css.muted, fontSize: ".86rem", paddingLeft: "1rem", position: "relative" }}>
                  <span style={{ position: "absolute", left: 0, color: ACCENT, fontWeight: 700 }}>›</span>
                  {pt}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Education ─────────────────────────────────────────────────────
function EducationPage() {
  return (
    <div style={{ ...css.wrap, fontFamily: "'Outfit', sans-serif", animation: "fadeUp .5s ease both" }}>
      <span style={css.label}>Academic background</span>
      <h2 style={css.h2}>Education</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        {EDUCATION_DATA.map((e, i) => (
          <div key={i} className="edu-card" style={{ ...css.card }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
              <div>
                <span style={{ ...css.label, marginBottom: ".3rem" }}>{e.year}</span>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#e8e8e8", marginBottom: ".25rem" }}>{e.degree}</h3>
                <div style={{ fontSize: ".84rem", color: "#555", marginBottom: ".4rem" }}>{e.field}</div>
                <div style={{ fontSize: ".84rem", color: ACCENT, fontWeight: 600 }}>{e.college}</div>
              </div>
              <span style={{
                padding: "4px 12px", borderRadius: 4, fontSize: ".72rem",
                fontWeight: 700, background: "#0d1a16", color: ACCENT,
                border: `1px solid ${ACCENT}33`, alignSelf: "flex-start",
              }}>Completed</span>
            </div>
          </div>
        ))}

        <div style={{
          ...css.card,
          borderLeft: `3px solid ${ACCENT}`,
          borderRadius: 10, marginTop: ".5rem",
        }}>
          <span style={{ ...css.label, marginBottom: ".75rem" }}>Certifications & Courses</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {CERTS.map((c) => (
              <span key={c} style={css.tag}>{c}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Contact ───────────────────────────────────────────────────────
function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const inputStyle = {
    width: "100%", padding: "10px 14px",
    background: "#0c0c0c", border: "1px solid #1e1e1e",
    borderRadius: 6, color: "#e8e8e8", fontSize: ".88rem",
    fontFamily: "'Outfit', sans-serif", transition: "border-color .2s",
  };

  return (
    <div style={{ ...css.wrap, fontFamily: "'Outfit', sans-serif", animation: "fadeUp .5s ease both" }}>
      <span style={css.label}>Let's connect</span>
      <h2 style={css.h2}>Contact</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: "3.5rem", alignItems: "start" }}>
        <div>
          <p style={{ ...css.muted, marginBottom: "2rem", fontSize: ".92rem" }}>
            I'm currently open to new opportunities — full-time roles, freelance
            projects, or just a good chat about Zoho or DevOps. Drop me a message!
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {[
              ["Email", "abimanyu@yourdomain.com"],
              ["LinkedIn", "linkedin.com/in/abimanyu"],
              ["GitHub", "github.com/abimanyu"],
              ["Location", "Madurai, Tamil Nadu, India"],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", gap: ".75rem", alignItems: "center" }}>
                <span style={{ ...css.label, margin: 0, minWidth: 72 }}>{k}</span>
                <span style={{ color: "#666", fontSize: ".86rem" }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{
            marginTop: "2.5rem", padding: "1.2rem",
            background: "#0c1a15", border: `1px solid ${ACCENT}22`,
            borderLeft: `3px solid ${ACCENT}`, borderRadius: 8,
          }}>
            <p style={{ ...css.muted, fontSize: ".84rem", color: "#555" }}>
              ⚡ Usually responds within 24 hours.
            </p>
          </div>
        </div>

        <div>
          {sent ? (
            <div style={{ textAlign: "center", padding: "3rem 0" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>✓</div>
              <div style={{ fontWeight: 700, color: ACCENT, fontSize: "1.1rem" }}>Message sent!</div>
              <p style={{ ...css.muted, marginTop: ".5rem" }}>I'll get back to you soon.</p>
              <button onClick={() => setSent(false)} style={{
                marginTop: "1.5rem", padding: "8px 20px",
                background: "transparent", border: "1px solid #222",
                color: "#666", borderRadius: 6, cursor: "pointer",
                fontFamily: "'Outfit', sans-serif", fontSize: ".84rem",
              }}>
                Send another
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                {["name", "email"].map((f) => (
                  <div key={f}>
                    <label style={{ ...css.label, marginBottom: ".4rem", textTransform: "capitalize" }}>{f}</label>
                    <input
                      value={form[f]}
                      onChange={set(f)}
                      placeholder={f === "name" ? "Your name" : "your@email.com"}
                      style={inputStyle}
                    />
                  </div>
                ))}
              </div>
              <div>
                <label style={{ ...css.label, marginBottom: ".4rem" }}>Subject</label>
                <input
                  value={form.subject}
                  onChange={set("subject")}
                  placeholder="Job opportunity / Freelance project / Just saying hi"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ ...css.label, marginBottom: ".4rem" }}>Message</label>
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell me about your project or opportunity..."
                  rows={5}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>
              <button
                onClick={() => { if (form.name && form.email && form.message) setSent(true); }}
                className="btn-primary"
                style={{
                  padding: "12px", background: ACCENT, color: BG,
                  border: "none", borderRadius: 6, fontWeight: 700,
                  fontSize: ".9rem", cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif", letterSpacing: ".04em",
                  transition: "opacity .15s",
                }}
              >
                Send Message →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── App Shell ─────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("Home");
  const [fading, setFading] = useState(false);

  const navigate = (dest) => {
    if (dest === page) return;
    setFading(true);
    setTimeout(() => {
      setPage(dest);
      setFading(false);
      window.scrollTo({ top: 0 });
    }, 220);
  };

  const renderPage = () => {
    switch (page) {
      case "Home":       return <HomePage navigate={navigate} />;
      case "About":      return <AboutPage />;
      case "Skills":     return <SkillsPage />;
      case "Projects":   return <ProjectsPage />;
      case "Experience": return <ExperiencePage />;
      case "Education":  return <EducationPage />;
      case "Contact":    return <ContactPage />;
      default:           return <HomePage navigate={navigate} />;
    }
  };

  return (
    <div style={{
      fontFamily: "'Outfit', sans-serif",
      background: BG, color: "#e8e8e8",
      minHeight: "100vh",
    }}>
      <Navbar page={page} navigate={navigate} />
      <div style={{
        paddingTop: 60,
        opacity: fading ? 0 : 1,
        transition: "opacity .22s ease",
      }}>
        {renderPage()}
      </div>

      {/* Footer */}
      <footer style={{
        borderTop: `1px solid ${BORDER}`,
        padding: "2rem",
        textAlign: "center",
        fontFamily: "'Outfit', sans-serif",
      }}>
        <p style={{ ...css.muted, fontSize: ".78rem" }}>
          Built with React · Deployed on Zoho Catalyst ·{" "}
          <span style={{ color: ACCENT }}>abimanyu.abimanyunk.com</span>
        </p>
      </footer>
    </div>
  );
}
